import React from 'react';

import {
    Anchor,
    Box,
    Collapsible,
    Divider,
    Flex,
    Heading,
    IconChevronDown,
    IconChevronUp,
    List,
    Paragraph,
    TextButton,
} from '@zillow/constellation';
import { useBotState, useMobileState } from '@zillow/shopper-platform-config';
import { yieldCallback } from '@zillow/yield-callback';

import { useGlobalStoreState } from '../../contexts/GlobalStoreProvider';
import type { LinkConfig } from './constants';
import { LINKS_BROWSE_HOMES, LINKS_MORTGAGE, LINKS_REAL_ESTATE, LINKS_RENTALS } from './constants';

const FooterContainerList: React.FC<{ title: string; links: LinkConfig[] }> = ({
    title,
    links,
}) => (
    <Box
        as={List.Item}
        sx={{
            'margin': 'auto $spacing.none',
            // dividers
            '[role="separator"]': {
                height: '$spacing.md',
            },
            // hide 'even' dividers when NOT in $lg
            '&:nth-child(even)': {
                '[role="separator"]': {
                    visibility: 'hidden',
                },
            },
            // hide ALL dividers when < xs
            [`@media $xs_lte`]: {
                '[role="separator"]': {
                    visibility: 'hidden',
                },
            },
            '@media $lg': {
                '[role="separator"]': {
                    height: '$spacing.lg',
                },
                // show 'even' dividers when IN $lg
                '&:nth-child(even)': {
                    '[role="separator"]': {
                        visibility: 'visible',
                    },
                },
                // except for the last one
                '&:last-of-type': {
                    '[role="separator"]': {
                        visibility: 'hidden',
                    },
                },
            },
        }}
    >
        <Collapsible
            defaultCollapsed
            // eslint-disable-next-line zillow/react/no-children-prop
            children={(collapsed, toggle) => (
                <React.Fragment>
                    <Flex
                        display="flex"
                        alignItems="center"
                        justifyContent={{ default: 'space-between', lg: 'space-around' }}
                    >
                        <Box
                            as={TextButton}
                            buttonType="secondary"
                            icon={
                                collapsed ? (
                                    <IconChevronDown fontColor="brand" />
                                ) : (
                                    <IconChevronUp fontColor="brand" />
                                )
                            }
                            iconSize="xs"
                            iconPosition="end"
                            sx={{
                                'display': 'flex',
                                'justifyContent': 'space-between',
                                'width': '100%',
                                'margin': '$spacing.none $spacing.sm',
                                '@media $lg': {
                                    justifyContent: 'center',
                                },
                            }}
                            onClick={yieldCallback(toggle)}
                        >
                            {title}
                        </Box>
                        <Divider orientation="vertical" marginX={0} />
                    </Flex>
                    <Box as={List} appearance="none" sx={{ display: collapsed ? 'none' : 'block' }}>
                        {links.map(({ href, text }) => (
                            <Box
                                as={List.Item}
                                key={href}
                                marginX="sm"
                                sx={{
                                    '@media $lg': {
                                        textAlign: 'center',
                                    },
                                }}
                            >
                                <Paragraph fontType="bodySmall">
                                    <Anchor href={href}>{text}</Anchor>
                                </Paragraph>
                            </Box>
                        ))}
                    </Box>
                </React.Fragment>
            )}
        />
    </Box>
);

const RegionFooterLinks: React.FC = () => {
    const { showNYFairHousingMsg } = useGlobalStoreState();
    const isBot = useBotState();
    const isMobile = useMobileState();

    const fairHousingNYPDF = `${
        isMobile ? 'https://docs.google.com/viewerng/viewer?url=' : ''
    }https://www.dos.ny.gov/licensing/docs/FairHousingNotice_new.pdf`;

    return (
        <Box
            sx={{
                maxWidth: '$breakpoints.xxl',
                backgroundColor: '$colors.backgroundWhite',
                margin: '0 auto',
            }}
        >
            {(showNYFairHousingMsg || isBot) && (
                <Box
                    as={Flex}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap="sm"
                    sx={{ margin: '$spacing.lg $spacing.none' }}
                >
                    <Heading level="5">You have a right to fair housing.</Heading>
                    <Paragraph fontType="bodySmall">
                        <Anchor href={fairHousingNYPDF}>
                            Learn about New York Fair Housing protections
                        </Anchor>
                    </Paragraph>
                </Box>
            )}

            <Box
                sx={{
                    [`@media $xs_lte`]: {
                        padding: '$spacing.none $spacing.md',
                    },
                    '@media $lg': {
                        padding: '$spacing.lg $spacing.md',
                    },
                    'padding': '$spacing.none $spacing.md',
                }}
            >
                <Box
                    as={List}
                    appearance="none"
                    sx={{
                        'display': 'grid',
                        'width': '100%',
                        'gridTemplateColumns': 'repeat(2, 1fr)',
                        '@media $lg': {
                            gridTemplateColumns: 'repeat(4, 1fr)',
                        },
                        [`@media $xs_lte`]: {
                            gridTemplateColumns: '1fr',
                        },
                    }}
                >
                    <FooterContainerList title="Real Estate" links={LINKS_REAL_ESTATE} />
                    <FooterContainerList title="Rentals" links={LINKS_RENTALS} />
                    <FooterContainerList title="Mortgage Rates" links={LINKS_MORTGAGE} />
                    <FooterContainerList title="Browse Homes" links={LINKS_BROWSE_HOMES} />
                </Box>
            </Box>
        </Box>
    );
};

export default RegionFooterLinks;
